import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import { Autocomplete, CircularProgress, TextField, ThemeProvider } from "@mui/material";
import { debounce } from "@mui/material/utils";

import "./composition.scss";
import { MUIComponentsTheme } from "../../services/theme-service/ThemeService";
import {
  TranslationContext,
  translationService,
} from "../../services/translation/TranslationService";
import { Close } from "../svg/svgImageList";
import Button from "../button/Button";
import { NameEntity } from "../../helpers/NameEntity";

export interface SuggestionEntity extends NameEntity {
  displayName?: string;
}

export interface SuggestionBox {
  defaultInput?: string;
  defaultSelectedItem?: SuggestionEntity | undefined;
  initialOptions?: SuggestionEntity[];
  searchTextChanged?: (searchText: string) => void;
  selectedItemChanged?: (item: SuggestionEntity) => void;
  onSearchTextChange: (searchText: string) => Promise<SuggestionEntity[]>;
  clearCurrentItem: () => void;
  placeholderKey?: string;
  placeholderContext?: TranslationContext;
  noResultsFoundKey?: string;
  noResultsFoundContext?: TranslationContext;
  disabled?: boolean;
  freeSolo?: boolean;
}

export default observer(function SuggestionBox(props: SuggestionBox) {
  const { searchTextChanged, selectedItemChanged, onSearchTextChange, clearCurrentItem } = props;
  const isDefaulSelectedValueFirstlyRendered = useRef(true);

  const [options, setOptions] = React.useState<SuggestionEntity[]>(
    props.initialOptions
      ? props.initialOptions
      : props.defaultSelectedItem
      ? [props.defaultSelectedItem]
      : []
  );
  const [loading, setIsLoading] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(props.defaultInput ?? "");
  const [value, setValue] = React.useState<SuggestionEntity | undefined>(
    props.defaultSelectedItem ?? undefined
  );

  const searchRequest = useCallback(
    async (searchText: string) => {
      var existingOption = options.find((x) => x.name === searchText);
      if (searchText === existingOption?.name || searchText === value?.name) return;

      setIsLoading(true);
      const filteredResult = await onSearchTextChange(searchText);
      setOptions(filteredResult);
      setIsLoading(false);
    },
    [onSearchTextChange, options, value?.name]
  );

  const debouncedSearchRequest = useMemo(() => {
    return debounce(searchRequest, 500);
  }, [searchRequest]);

  const handleSelectedItemChange = useCallback(
    (newValue: SuggestionEntity) => {
      setValue(newValue);
      selectedItemChanged && selectedItemChanged(newValue);
    },
    [selectedItemChanged]
  );

  const clearSelectedItem = useCallback(() => {
    setInputValue("");
    searchTextChanged && searchTextChanged("");
    setValue(undefined);
    setOptions(props.initialOptions ?? []);
    clearCurrentItem();
  }, [props.initialOptions, searchTextChanged, clearCurrentItem]);

  const onSearchTextChanged = useCallback(
    (searchText: string) => {
      setInputValue(searchText);
      searchTextChanged && searchTextChanged(searchText);

      if (!searchText) {
        clearSelectedItem();
        return;
      }

      debouncedSearchRequest(searchText);
    },
    [clearSelectedItem, debouncedSearchRequest, searchTextChanged]
  );

  const getTranslatedPlaceholder = useMemo(
    () =>
      props.placeholderKey
        ? translationService.translate(props.placeholderKey, props.placeholderContext)
        : undefined,
    [props.placeholderContext, props.placeholderKey]
  );

  const getTranslatedNoResultsFoundText = useMemo(
    () =>
      props.noResultsFoundKey
        ? translationService.translate(props.noResultsFoundKey, props.noResultsFoundContext)
        : undefined,
    [props.noResultsFoundContext, props.noResultsFoundKey]
  );

  const getTranslatedLoadingText = useMemo(() => {
    const translatedLoading = translationService.translate("general.loading");
    return `${translatedLoading[0].toUpperCase() + translatedLoading.slice(1)}...`;
  }, []);

  useEffect(() => {
    setInputValue(props.defaultInput ?? "");
  }, [props.defaultInput]);

  useEffect(() => {
    if (isDefaulSelectedValueFirstlyRendered.current) {
      isDefaulSelectedValueFirstlyRendered.current = false;
      return;
    }
    if (props.defaultSelectedItem) {
      setValue(props.defaultSelectedItem);
      setOptions(props.initialOptions ?? [props.defaultSelectedItem]);
    } else setValue(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.defaultSelectedItem]);

  return (
    <ThemeProvider theme={MUIComponentsTheme}>
      <Autocomplete
        className="suggestion-box"
        id="autocomplete"
        disablePortal
        freeSolo={props.freeSolo ?? false}
        value={props.freeSolo ? null : value || null}
        inputValue={inputValue}
        filterOptions={(x) => x}
        options={options}
        getOptionLabel={(option: any) =>
          (option as SuggestionEntity)?.displayName ?? (option as SuggestionEntity)?.name ?? ""
        }
        isOptionEqualToValue={(option, value) => option?.displayName === value?.displayName}
        loading={loading}
        loadingText={getTranslatedLoadingText}
        onChange={(_: any, newValue: SuggestionEntity | string | null, reason) => {
          if (reason === "selectOption") handleSelectedItemChange(newValue as SuggestionEntity);
        }}
        onInputChange={(event: any, searchText: string, reason: any) => {
          if (reason === "input") onSearchTextChanged(searchText);
          if (!props.freeSolo && reason === "reset" && value && event?._reactName !== "onBlur")
            setInputValue(searchText);
        }}
        disabled={!!props.disabled}
        noOptionsText={getTranslatedNoResultsFoundText}
        renderOption={(props, option) => {
          return (
            <span {...props} className="option">
              {(option as SuggestionEntity)?.displayName ?? (option as SuggestionEntity)?.name}
            </span>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            InputProps={{
              ...params.InputProps,
              placeholder: getTranslatedPlaceholder,
              endAdornment: (
                <div className="end-adornment">
                  {loading && <CircularProgress size={20} />}
                  {inputValue && !props.disabled && (
                    <Button
                      id="suggestion-clear-selection"
                      onClick={clearSelectedItem}
                      icon={{ svg: Close, width: 11, height: 11 }}
                    />
                  )}
                </div>
              ),
            }}
          />
        )}
      />
    </ThemeProvider>
  );
});
